/**
 * Inbox API calls, reconciled with workers/api/src/inbox/routes.ts (R10). `record`, `lastInboundAt`, `buttons`, `templateName`
 * and `sentBy` are not sent by the server today; they stay optional and the UI falls back without them.
 * Where the routes are not deployed the server answers 404, and the Inbox renders "Not connected yet".
 */
import type { ApiClient } from "./client";

/** The three tabs. An unmatched number (`unknown` on the server) is listed under Leads as a "New contact". */
export type InboxSegment = "lead" | "member" | "staff";
export type ConversationKind = InboxSegment | "unknown";
export type MessageStatus = "pending" | "sent" | "delivered" | "read" | "failed";

export interface InboxNumber { brandId: string; brandName: string; displayPhone: string }
export interface SegmentCount { unread: number; total: number }
export type InboxCounts = Record<InboxSegment, SegmentCount>;

export interface Conversation {
  id: string;
  kind: ConversationKind;
  name: string | null;
  /** Masked (`******1234`) unless the role has `see_phone`. */
  phone: string;
  brandId: string;
  branchId: string | null;
  assignee: { id: string; name: string } | null;
  /** Plan name or lead stage, when the server knows it (assumed field). */
  record?: { label: string; tone?: "ok" | "warn" | "lapsed" | "info" | "violet" | "teal" | "neutral" } | null;
  lastPreview: string | null;
  lastDirection: "in" | "out" | null;
  lastStatus: MessageStatus | null;
  lastMessageAt: number;
  lastInboundAt?: number | null;
  unreadCount: number;
  /** Epoch ms; the 24h window is open while `now < windowOpenUntil`. Null = never wrote. */
  windowOpenUntil: number | null;
  archived: boolean;
  memberId?: string | null;
  leadId?: string | null;
  staffId?: string | null;
}

export interface PageKey { before: number; beforeId: string }
export interface ConversationPage { conversations: Conversation[]; next: PageKey | null }
export interface ThreadPage { messages: ThreadMessage[]; next?: PageKey | null }

export interface ThreadMessage {
  id: string;
  direction: "in" | "out";
  kind: "text" | "template" | "media" | "system";
  body: string | null;
  templateKey?: string | null;
  templateName?: string | null;
  /** Inert buttons of an approved template. */
  buttons?: string[];
  sentBy?: { id: string; name: string } | null;
  sentByStaffId?: string | null;
  status: MessageStatus | null;
  at: number;
}

export interface InboxTemplate { key: string; name: string; category: string; body: string; params: string[]; brandId?: string }
interface WireTemplate { brandId: string; templateKey: string; name: string; category: string; bodyPreview: string; params: string[] }

/** The server names an unmatched contact "New contact"; here a null name means that, so it is never greeted as "New". */
const toConversation = (c: Conversation): Conversation => (c.kind === "unknown" && c.name === "New contact" ? { ...c, name: null } : c);
/** One row per template, with the copy's positional `{{1}}` turned into the `{{param}}` the preview fills. */
function toTemplates(rows: WireTemplate[]): InboxTemplate[] {
  const byKey = new Map<string, InboxTemplate>();
  for (const r of rows) {
    if (!byKey.has(r.templateKey)) byKey.set(r.templateKey, { key: r.templateKey, name: r.name, category: r.category, brandId: r.brandId, params: r.params, body: r.bodyPreview.replace(/\{\{(\d+)\}\}/g, (m, n: string) => { const p = r.params[Number(n) - 1]; return p ? `{{${p}}}` : m; }) });
  }
  return [...byKey.values()];
}
export interface Assignee { id: string; name: string }

export interface ListQuery {
  kind: InboxSegment;
  brandId?: string;
  assignee?: "me" | "unassigned";
  unread?: boolean;
  archived?: boolean;
  q?: string;
  before?: number;
  beforeId?: string;
  limit?: number;
}

export const fetchInboxNumbers = (c: ApiClient) => c.get<{ numbers: InboxNumber[] }>("/inbox/numbers").then((r) => r.numbers);
export const fetchInboxCounts = (c: ApiClient, brandId?: string) => c.get<InboxCounts>("/inbox/counts", { brandId });
export const fetchConversations = (c: ApiClient, q: ListQuery) =>
  c.get<ConversationPage>("/inbox/conversations", { ...q, unread: q.unread ? 1 : undefined, archived: q.archived ? 1 : undefined, limit: q.limit ?? 30 }).then((r) => ({ ...r, conversations: r.conversations.map(toConversation) }));
export const fetchConversation = (c: ApiClient, id: string) => c.get<{ conversation: Conversation } | Conversation>(`/inbox/conversations/${id}`).then((r) => toConversation("conversation" in r ? r.conversation : r));
export const fetchThread = (c: ApiClient, id: string, page?: PageKey | null) =>
  c.get<ThreadPage>(`/inbox/conversations/${id}/messages`, { ...(page ?? {}), limit: 50 });
/** With a conversation, only staff who can take that chat; the PATCH refuses anyone else. */
export const fetchAssignees = (c: ApiClient, conversationId?: string) => c.get<{ assignees: Assignee[] }>("/inbox/assignees", { conversationId }).then((r) => r.assignees);
export const fetchInboxTemplates = (c: ApiClient, brandId?: string) => c.get<{ templates: WireTemplate[] }>("/inbox/templates", { brandId }).then((r) => toTemplates(r.templates));

/** One Idempotency-Key per composer intent: pass the same key on a retry (R2). */
export const sendTemplate = (c: ApiClient, id: string, v: { templateKey: string; params: Record<string, string> }, idempotencyKey: string) =>
  c.post<{ message: ThreadMessage }>(`/inbox/conversations/${id}/template`, v, { idempotencyKey });
export const sendText = (c: ApiClient, id: string, v: { body: string }, idempotencyKey: string) =>
  c.post<{ message: ThreadMessage }>(`/inbox/conversations/${id}/text`, v, { idempotencyKey });
export const patchConversation = (c: ApiClient, id: string, v: { assigneeStaffId?: string | null; read?: boolean; archived?: boolean }) =>
  c.patch<{ conversation?: Conversation }>(`/inbox/conversations/${id}`, v);
